import React, { useState, useEffect, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/Textarea";
import { supabase } from "@/lib/supabase";
import { FileText, Mail, MapPin, X, AlertTriangle, Users } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Carga perezosa de modales pesados para reducir coste inicial en tablets
const SepaConsentPreviewLazy = React.lazy(() => import('./SepaConsentPreview').then(m => ({ default: m.SepaConsentPreview })));
const ShippingAddressFormLazy = React.lazy(() => import('./ShippingAddressForm').then(m => ({ default: m.ShippingAddressForm })));
import { ShippingAddressList } from './ShippingAddressList';
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { USER_DIRECTORY_FIELDS, USERS_DIRECTORY_VIEW } from "@/lib/userAccess";

export function CustomerForm({ customer, onSubmit, onCancel, userCodRep, canViewAllCustomers }) {
  const [formData, setFormData] = useState({
    name: customer?.name || "",
    titular: customer?.titular || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    movil: customer?.movil || "",
    street: customer?.street || "",
    city: customer?.city || "",
    province: customer?.province || "",
    postalCode: customer?.postalCode || "",
    code: customer?.code || "",
    cif: customer?.cif || "",
    cod_rep: customer?.cod_rep || userCodRep || "",
    iban: customer?.iban || "",
    sepa_signature: customer?.sepa_signature || null,
    recargo_equivalencia: customer?.recargo_equivalencia ?? false,
    incidencias: customer?.incidencias || ""
  });

  const [users, setUsers] = useState([]);
  const [showSepaPreview, setShowSepaPreview] = useState(false);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [representativeName, setRepresentativeName] = useState("");
  const [errors, setErrors] = useState({});
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [isSendingSepaRequest, setIsSendingSepaRequest] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
    if (customer?.id) {
      fetchShippingAddresses();
    }
    if (!customer && userCodRep) {
      setFormData(prev => ({
        ...prev,
        cod_rep: userCodRep
      }));
    }
    if (formData.cod_rep) {
      fetchRepresentativeName(formData.cod_rep);
    }
  }, [customer?.id, userCodRep, formData.cod_rep]);

  const fetchUsers = async () => {
    try {
      const { data: usersData, error } = await supabase
        .from(USERS_DIRECTORY_VIEW)
        .select(USER_DIRECTORY_FIELDS)
        .eq('role', 'vendedor');

      if (error) throw error;

      const vendedores = usersData.map(user => ({
        id: user.id,
        username: user.name || user.cod_rep || user.id
      }));

      setUsers(vendedores);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchShippingAddresses = async () => {
    try {
      const { data, error } = await supabase
        .from('shipping_addresses')
        .select('*')
        .eq('customer_id', customer.id)
        .order('is_default', { ascending: false });

      if (error) throw error;
      setShippingAddresses(data || []);
    } catch (error) {
      console.error('Error fetching shipping addresses:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las direcciones de envío",
        variant: "destructive"
      });
    }
  };

  const fetchRepresentativeName = async (codRep) => {
    try {
      const { data, error } = await supabase
        .from(USERS_DIRECTORY_VIEW)
        .select(USER_DIRECTORY_FIELDS)
        .eq('cod_rep', codRep)
        .single();

      if (error) throw error;
      setRepresentativeName(data?.name || "");
    } catch (error) {
      console.error('Error fetching representative name:', error);
      setRepresentativeName("");
    }
  };

  // Función para validar email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Función para validar el formulario
  const validateForm = () => {
    const newErrors = {};

    // NOTA: en producción se permite alta rápida con ficha incompleta.
    // Se mantiene solo el mínimo técnico para evitar error DB.
    if (!formData.name?.trim()) {
      newErrors.name = "El nombre fiscal es obligatorio";
    }

    // Validar email si está informado
    if (formData.email?.trim() && !validateEmail(formData.email)) {
      newErrors.email = "El formato del email no es válido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Error de validación",
        description: "Por favor, corrija los errores en el formulario",
        variant: "destructive"
      });
      return;
    }

    // Verificar si hay duplicados antes de guardar
    if (duplicates.length > 0) {
      const hasExactCIF = duplicates.some(d => d.matchType === 'CIF/NIF exacto');
      const hasHighSimilarity = duplicates.some(d => d.similarity >= 90);
      
      let message = "Se han detectado posibles duplicados. ";
      if (hasExactCIF) {
        message += "Ya existe un cliente con el mismo CIF/NIF. ";
      }
      if (hasHighSimilarity) {
        message += "Existen clientes con nombres muy similares. ";
      }
      message += "¿Está seguro de que desea continuar?";
      
      const confirmed = window.confirm(message);
      if (!confirmed) {
        return;
      }
    }

    const submitData = {
      ...formData,
      incidencias: formData.incidencias?.trim() || null,
      id: customer?.id
    };
    onSubmit(submitData);
  };

  const handleSaveSignature = async (signatureData) => {
    try {
      // Si el cliente ya existe, persistimos en DB al momento.
      // Si es alta nueva, guardamos en estado local y se insertará al guardar cliente.
      if (customer?.id) {
        const { error } = await supabase
          .from('customers')
          .update({ sepa_signature: signatureData })
          .eq('id', customer.id);

        if (error) throw error;
      }

      setFormData(prev => ({
        ...prev,
        sepa_signature: signatureData
      }));
    } catch (error) {
      console.error('Error guardando firma:', error);
      alert('Error al guardar la firma');
    }
  };

  const handleSaveShippingAddress = async (addressData) => {
    try {
      if (editingAddress) {
        const { error } = await supabase
          .from('shipping_addresses')
          .update(addressData)
          .eq('id', editingAddress.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('shipping_addresses')
          .insert(addressData);

        if (error) throw error;
      }

      await fetchShippingAddresses();
      setShowShippingForm(false);
      setEditingAddress(null);
      toast({
        title: "Dirección guardada",
        description: "La dirección de envío se ha guardado correctamente",
      });
    } catch (error) {
      console.error('Error saving shipping address:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar la dirección de envío",
        variant: "destructive"
      });
      throw error; // Re-throw para que el formulario sepa que hubo un error
    }
  };

  const handleDeleteShippingAddress = async (addressId) => {
    // Mostrar confirmación antes de eliminar
    const confirmed = window.confirm(
      "¿Está seguro de que desea eliminar esta dirección de envío? Esta acción no se puede deshacer."
    );
    
    if (!confirmed) {
      return;
    }

    try {
      const { error } = await supabase
        .from('shipping_addresses')
        .delete()
        .eq('id', addressId);

      if (error) throw error;

      await fetchShippingAddresses();
      toast({
        title: "Dirección eliminada",
        description: "La dirección de envío se ha eliminado correctamente",
      });
    } catch (error) {
      console.error('Error deleting shipping address:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la dirección de envío",
        variant: "destructive"
      });
    }
  };

  const handleEditShippingAddress = (address) => {
    setEditingAddress(address);
    setShowShippingForm(true);
  };

  const hasMandateIncompleteData = Boolean(formData.sepa_signature) && (
    !formData.iban?.trim() ||
    !formData.street?.trim() ||
    !formData.city?.trim() ||
    !formData.province?.trim() ||
    !formData.postalCode?.trim()
  );

  const getMandateMissingFields = () => {
    const missing = [];

    if (!formData.email?.trim()) missing.push("email");
    if (!formData.iban?.trim()) missing.push("IBAN");
    if (!formData.street?.trim()) missing.push("calle");
    if (!formData.city?.trim()) missing.push("población");
    if (!formData.province?.trim()) missing.push("provincia");
    if (!formData.postalCode?.trim()) missing.push("código postal");

    return missing;
  };

  const handleSendSepaRequest = async () => {
    if (!customer?.id) {
      toast({
        title: "Guarde el cliente primero",
        description: "El enlace SEPA solo se puede enviar cuando la ficha ya existe.",
        variant: "destructive"
      });
      return;
    }

    if (!validateEmail(formData.email || "")) {
      toast({
        title: "Email no válido",
        description: "Informe un email válido antes de enviar el consentimiento SEPA.",
        variant: "destructive"
      });
      return;
    }

    const missingFields = getMandateMissingFields();
    if (missingFields.length > 0) {
      toast({
        title: "Datos SEPA incompletos",
        description: `Complete ${missingFields.join(", ")} antes de enviar el consentimiento.`,
        variant: "destructive"
      });
      return;
    }

    setIsSendingSepaRequest(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-sepa-signature-request", {
        body: {
          customerId: customer.id,
          recipientEmail: formData.email
        }
      });

      if (error) throw error;

      toast({
        title: "Consentimiento enviado",
        description: `Enlace SEPA enviado a ${data?.sentTo || formData.email}.`,
      });
    } catch (error) {
      console.error("Error enviando consentimiento SEPA:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo enviar el consentimiento SEPA",
        variant: "destructive"
      });
    } finally {
      setIsSendingSepaRequest(false);
    }
  };

  const formatIBAN = (value) => {
    // Eliminar todos los espacios y caracteres no alfanuméricos
    const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    
    // Si no empieza con ES, añadirlo
    let ibanValue = cleaned;
    if (!ibanValue.startsWith('ES')) {
      ibanValue = 'ES' + ibanValue;
    }
    
    // Formatear en bloques: ES XX XXXX XXXX XX XXXXXXXXXX
    let formatted = '';
    if (ibanValue.length > 0) formatted += ibanValue.slice(0, 2); // ES
    if (ibanValue.length > 2) formatted += ' ' + ibanValue.slice(2, 4); // DC
    if (ibanValue.length > 4) formatted += ' ' + ibanValue.slice(4, 8); // Entidad
    if (ibanValue.length > 8) formatted += ' ' + ibanValue.slice(8, 12); // Oficina
    if (ibanValue.length > 12) formatted += ' ' + ibanValue.slice(12, 14); // DC
    if (ibanValue.length > 14) formatted += ' ' + ibanValue.slice(14, 24); // Número de cuenta
    
    return formatted;
  };

  const handleIBANChange = (e) => {
    const formattedValue = formatIBAN(e.target.value);
    setFormData({ ...formData, iban: formattedValue });
  };

  // Función para calcular similitud entre dos strings
  const calculateSimilarity = (str1, str2) => {
    if (!str1 || !str2) return 0;
    
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();
    
    if (s1 === s2) return 100;
    
    // Algoritmo simple de similitud basado en caracteres comunes
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    
    if (longer.length === 0) return 100;
    
    const editDistance = levenshteinDistance(longer, shorter);
    return ((longer.length - editDistance) / longer.length) * 100;
  };

  // Algoritmo de distancia de Levenshtein
  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  // Función para buscar duplicados
  const checkDuplicates = async (openModalOnResults = false) => {
    const hasValidCIF = formData.cif?.trim() && formData.cif.trim().length >= 3;
    const hasValidName = formData.name?.trim() && formData.name.trim().length >= 3;
    
    if (!hasValidCIF && !hasValidName) {
      setDuplicates([]);
      return;
    }

    setIsCheckingDuplicates(true);
    const foundDuplicates = [];

    try {
      // Buscar por CIF/NIF exacto
      if (hasValidCIF) {
        let query = supabase
          .from('customers')
          .select('id, name, cif, city, province, email, phone')
          .eq('cif', formData.cif.trim());
        
        // Solo excluir el cliente actual si estamos editando
        if (customer?.id) {
          query = query.neq('id', customer.id);
        }

        const { data: cifDuplicates, error: cifError } = await query;

        if (!cifError && cifDuplicates?.length > 0) {
          foundDuplicates.push(...cifDuplicates.map(dup => ({
            ...dup,
            matchType: 'CIF/NIF exacto',
            similarity: 100
          })));
        }
      }

      // Buscar por similitud de nombre (limitado por servidor para evitar traer toda la tabla)
      if (hasValidName) {
        const searchName = formData.name.trim();
        let query = supabase
          .from('customers')
          .select('id, name, cif, city, province, email, phone')
          // Limitar por coincidencia parcial para reducir dataset en cliente
          .ilike('name', `%${searchName.slice(0, 5)}%`)
          .limit(200);
        
        // Solo excluir el cliente actual si estamos editando
        if (customer?.id) {
          query = query.neq('id', customer.id);
        }

        const { data: allCustomers, error: nameError } = await query;

        if (!nameError && allCustomers) {
          const nameSimilarities = allCustomers
            .map(cust => ({
              ...cust,
              similarity: calculateSimilarity(formData.name, cust.name)
            }))
            .filter(cust => cust.similarity >= 80) // Solo mostrar si similitud >= 80%
            .sort((a, b) => b.similarity - a.similarity)
            .slice(0, 20); // Limitar resultados para UI en dispositivos con menos recursos

          foundDuplicates.push(...nameSimilarities.map(dup => ({
            ...dup,
            matchType: `Similitud de nombre (${Math.round(dup.similarity)}%)`
          })));
        }
      }

      // Eliminar duplicados por ID
      const uniqueDuplicates = foundDuplicates.filter((dup, index, self) => 
        index === self.findIndex(d => d.id === dup.id)
      );

      setDuplicates(uniqueDuplicates);
      
      if (uniqueDuplicates.length > 0 && openModalOnResults) {
        setShowDuplicatesModal(true);
      }

    } catch (error) {
      console.error('Error checking duplicates:', error);
      toast({
        title: "Error",
        description: "No se pudieron verificar los duplicados",
        variant: "destructive"
      });
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  // Verificar duplicados cuando cambian CIF o nombre
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Solo verificar si hay suficiente información
      const hasValidCIF = formData.cif?.trim() && formData.cif.trim().length >= 3;
      const hasValidName = formData.name?.trim() && formData.name.trim().length >= 3;
      
      if (hasValidCIF || hasValidName) {
        checkDuplicates();
      } else {
        setDuplicates([]);
      }
    }, 1500); // Debounce de 1.5 segundos

    return () => clearTimeout(timeoutId);
  }, [formData.cif, formData.name]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm relative">
      {/* Botón de cerrar en la esquina superior derecha */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="absolute top-4 right-4 h-8 w-8 p-0 hover:bg-gray-100"
        title="Cerrar formulario"
      >
        <X className="h-4 w-4" />
      </Button>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Columna izquierda - Datos Fiscales */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">
              Datos Fiscales
            </h3>
            
            <div>
              <label className="text-sm font-medium mb-1">CIF/NIF</label>
              <div className="relative">
                <Input
                  value={formData.cif}
                  onChange={(e) => setFormData({ ...formData, cif: e.target.value })}
                  placeholder="CIF/NIF del cliente"
                  className={`${errors.cif ? "border-red-500" : ""} ${
                    duplicates.some(d => d.matchType === 'CIF/NIF exacto') ? "border-yellow-500 bg-yellow-50" : ""
                  }`}
                />
                {isCheckingDuplicates && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  </div>
                )}
                {duplicates.some(d => d.matchType === 'CIF/NIF exacto') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  </div>
                )}
              </div>
              {errors.cif && (
                <p className="text-sm text-red-500 mt-1">{errors.cif}</p>
              )}
              {duplicates.some(d => d.matchType === 'CIF/NIF exacto') && (
                <p className="text-sm text-yellow-600 mt-1 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Ya existe un cliente con este CIF/NIF
                </p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Nombre Fiscal *</label>
              <div className="relative">
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre fiscal del cliente"
                  className={`${errors.name ? "border-red-500" : ""} ${
                    duplicates.some(d => d.matchType.includes('Similitud')) ? "border-yellow-500 bg-yellow-50" : ""
                  }`}
                />
                {isCheckingDuplicates && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  </div>
                )}
                {duplicates.some(d => d.matchType.includes('Similitud')) && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Users className="h-4 w-4 text-yellow-500" />
                  </div>
                )}
              </div>
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name}</p>
              )}
              {duplicates.some(d => d.matchType.includes('Similitud')) && (
                <p className="text-sm text-yellow-600 mt-1 flex items-center">
                  <Users className="h-3 w-3 mr-1" />
                  Se encontraron clientes con nombres similares
                </p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Calle</label>
              <Input
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                placeholder="Calle del cliente"
                className={errors.street ? "border-red-500" : ""}
              />
              {errors.street && (
                <p className="text-sm text-red-500 mt-1">{errors.street}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Población</label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Población"
                className={errors.city ? "border-red-500" : ""}
              />
              {errors.city && (
                <p className="text-sm text-red-500 mt-1">{errors.city}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Provincia</label>
              <Input
                value={formData.province}
                onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                placeholder="Provincia"
                className={errors.province ? "border-red-500" : ""}
              />
              {errors.province && (
                <p className="text-sm text-red-500 mt-1">{errors.province}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Código Postal</label>
              <Input
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                placeholder="Código Postal"
                className={errors.postalCode ? "border-red-500" : ""}
              />
              {errors.postalCode && (
                <p className="text-sm text-red-500 mt-1">{errors.postalCode}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">IBAN</label>
              <Input
                value={formData.iban}
                onChange={handleIBANChange}
                placeholder="ES XX XXXX XXXX XX XXXXXXXXXX"
                maxLength={29}
                className="font-mono"
              />
              <p className="text-sm text-gray-500 mt-1">
                Formato: ES XX XXXX XXXX XX XXXXXXXXXX
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="recargo_equivalencia"
                type="checkbox"
                checked={formData.recargo_equivalencia}
                onChange={(e) => setFormData({ ...formData, recargo_equivalencia: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor="recargo_equivalencia" className="text-sm font-medium">
                Recargo de equivalencia
              </label>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Código Alfawin</label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="Código del cliente (se asigna automáticamente desde el ERP)"
                disabled={true}
                className="bg-gray-100"
              />
              <p className="text-sm text-gray-500 mt-1">
                El código Alfawin se asigna automáticamente desde el sistema ERP durante las importaciones
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => checkDuplicates(true)}
                disabled={isCheckingDuplicates || (!formData.cif?.trim() && !formData.name?.trim())}
                className="items-center"
              >
                {isCheckingDuplicates ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                ) : (
                  <Users className="h-4 w-4 mr-2" />
                )}
                Verificar duplicados
              </Button>
              {duplicates.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {duplicates.length} posible(s) duplicado(s)
                </Badge>
              )}
            </div>
          </div>

          {/* Columna derecha - Datos Comerciales */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">
              Datos Comerciales
            </h3>
            
            <div>
              <label className="text-sm font-medium mb-1">Nombre Comercial</label>
              <Input
                value={formData.titular}
                onChange={(e) => setFormData({ ...formData, titular: e.target.value })}
                placeholder="Nombre comercial del cliente"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email del cliente"
                className={errors.email ? "border-red-500" : ""}
              />
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">{errors.email}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Teléfono</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Teléfono del cliente"
                className={errors.phone ? "border-red-500" : ""}
              />
              {errors.phone && (
                <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Teléfono móvil</label>
              <Input
                value={formData.movil}
                onChange={(e) => setFormData({ ...formData, movil: e.target.value })}
                placeholder="Teléfono móvil del cliente"
                className={errors.movil ? "border-red-500" : ""}
              />
              {errors.movil && (
                <p className="text-sm text-red-500 mt-1">{errors.movil}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1">Código de Representante</label>
              <Input
                value={formData.cod_rep}
                onChange={(e) => setFormData({ ...formData, cod_rep: e.target.value })}
                placeholder="Código del representante"
                disabled={!canViewAllCustomers}
              />
              {representativeName && (
                <p className="text-sm text-gray-500 mt-1">
                  Representante: {representativeName}
                </p>
              )}
            </div>

            <Textarea
              label="Incidencias"
              name="customer-incidencias"
              value={formData.incidencias}
              onChange={(e) => setFormData({ ...formData, incidencias: e.target.value })}
              placeholder="Anota aquí cualquier incidencia relevante del cliente"
              className="w-full min-h-[120px] border border-amber-200 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y"
            />
            
            <div className="mt-2 space-y-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setShowSepaPreview(true)}
              >
                <FileText className="mr-2 h-4 w-4" />
                {formData.sepa_signature ? "Ver Consentimiento SEPA" : "Firmar Consentimiento SEPA"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleSendSepaRequest}
                disabled={isSendingSepaRequest || !customer?.id}
              >
                <Mail className="mr-2 h-4 w-4" />
                {isSendingSepaRequest ? "Enviando..." : "Enviar SEPA por email"}
              </Button>
              {hasMandateIncompleteData && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Mandato SEPA firmado con datos incompletos. Complete IBAN y dirección del deudor.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Sección de direcciones de envío - Ocupa todo el ancho */}
        {customer?.id && (
          <div className="mt-8 border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Direcciones de Envío</h3>
              <Button
                type="button"
                onClick={() => {
                  setEditingAddress(null);
                  setShowShippingForm(true);
                }}
              >
                <MapPin className="mr-2 h-4 w-4" />
                Nueva Dirección
              </Button>
            </div>
            <ShippingAddressList
              addresses={shippingAddresses}
              onEdit={handleEditShippingAddress}
              onDelete={handleDeleteShippingAddress}
            />
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit">
            {customer ? "Actualizar" : "Guardar"}
          </Button>
        </div>

        <Suspense fallback={null}>
          <SepaConsentPreviewLazy
            customer={formData}
            open={showSepaPreview}
            onOpenChange={setShowSepaPreview}
            onSave={handleSaveSignature}
          />
        </Suspense>

        <Suspense fallback={null}>
          <ShippingAddressFormLazy
            open={showShippingForm}
            onOpenChange={setShowShippingForm}
            onSubmit={handleSaveShippingAddress}
            customerId={customer?.id}
            address={editingAddress}
          />
        </Suspense>
      </form>

             {/* Modal de duplicados */}
       <Dialog open={showDuplicatesModal} onOpenChange={setShowDuplicatesModal}>
         <DialogContent className="max-w-2xl">
           <DialogHeader>
             <DialogTitle className="flex items-center">
               <AlertTriangle className="mr-2 h-5 w-5 text-red-500" />
               Posibles duplicados encontrados
             </DialogTitle>
             <DialogDescription>
               Se han encontrado {duplicates.length} cliente(s) que podrían ser duplicados.
               Por favor, revise la información antes de continuar.
             </DialogDescription>
           </DialogHeader>
           <div className="space-y-3 max-h-96 overflow-y-auto">
             {duplicates.map((dup, index) => (
               <div key={dup.id} className="border rounded-lg p-4 bg-gray-50">
                 <div className="flex items-start justify-between mb-2">
                   <h4 className="font-semibold text-gray-800 text-lg">{dup.name}</h4>
                   <Badge 
                     variant={dup.matchType.includes('exacto') ? "destructive" : "secondary"}
                     className="text-xs"
                   >
                     {dup.matchType}
                   </Badge>
                 </div>
                 <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                   <div>
                     <span className="font-medium">CIF/NIF:</span> {dup.cif || 'No informado'}
                   </div>
                   <div>
                     <span className="font-medium">Población:</span> {dup.city || 'No informada'}
                   </div>
                   <div>
                     <span className="font-medium">Provincia:</span> {dup.province || 'No informada'}
                   </div>
                   <div>
                     <span className="font-medium">Email:</span> {dup.email || 'No informado'}
                   </div>
                   <div>
                     <span className="font-medium">Teléfono:</span> {dup.phone || 'No informado'}
                   </div>
                   {dup.similarity && (
                     <div>
                       <span className="font-medium">Similitud:</span> {Math.round(dup.similarity)}%
                     </div>
                   )}
                 </div>
               </div>
             ))}
           </div>
           <div className="flex justify-between items-center mt-6 pt-4 border-t">
             <div className="text-sm text-gray-500">
               <p>• <strong>CIF/NIF exacto:</strong> Mismo número fiscal</p>
               <p>• <strong>Similitud de nombre:</strong> Nombres muy similares (≥80%)</p>
             </div>
             <div className="flex gap-2">
               <Button variant="outline" onClick={() => setShowDuplicatesModal(false)}>
                 Continuar de todas formas
               </Button>
               <Button 
                 variant="destructive" 
                 onClick={() => {
                   setShowDuplicatesModal(false);
                   setFormData(prev => ({ ...prev, cif: '', name: '' }));
                 }}
               >
                 Limpiar campos
               </Button>
             </div>
           </div>
         </DialogContent>
       </Dialog>
    </div>
  );
}
